import React, { useCallback, useEffect, useRef } from 'react';
import BuildProgressTab, { ProgressStatusMessage } from './BuildProgressTab';
import type { AiActivitySummary } from '../../../../../shared/lib/buildStatusTracker';
import BuildLogsTab from './BuildLogsTab';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';

interface BuildBottomPanelProps {
    isOpen: boolean;
    height: number;
    isResizing: boolean;
    activeTab: 'progress' | 'logs';
    progressStatusMessages: ProgressStatusMessage[];
    aiActivitySummary?: AiActivitySummary | null;
    progressTabEndRef: React.RefObject<HTMLDivElement | null>;
    dockerLogs: string;
    processedLogs: string;
    onToggle: () => void;
    onTabChange: (tab: 'progress' | 'logs') => void;
    onResizeStart: (e: React.PointerEvent<HTMLDivElement>) => void;
    onClearLogs: () => void;
    onToggleConnection: () => void;
}

const BuildBottomPanel: React.FC<BuildBottomPanelProps> = ({
    isOpen,
    height,
    isResizing,
    activeTab,
    progressStatusMessages,
    aiActivitySummary,
    progressTabEndRef,
    dockerLogs,
    processedLogs,
    onToggle,
    onTabChange,
    onResizeStart,
    onClearLogs,
    onToggleConnection
}) => {
    const wsStatus = useWebSocketStatus();
    const logsEndRef = useRef<HTMLDivElement | null>(null);

    // Auto-scroll logs tab to bottom when logs change (kept for backward compatibility with old terminal display)
    useEffect(() => {
        if (activeTab === 'logs' && logsEndRef.current) {
            logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
        }
    }, [dockerLogs, processedLogs, activeTab]);

    // Scroll to bottom when logs tab becomes active (on mount or tab switch)
    useEffect(() => {
        if (activeTab === 'logs' && logsEndRef.current) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
                if (logsEndRef.current) {
                    logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [activeTab]);

    if (!isOpen) {
        return null;
    }

    return (
        <div 
            className="fixed bottom-0 left-0 right-0 bg-gray-900 flex flex-col transform z-50"
            style={{ 
                height: `${height}px`, 
                maxHeight: '800px', 
                minHeight: '200px',
                transition: isResizing ? 'none' : 'transform 300ms ease-in-out'
            }}
        >
            {/* Resize Handle */}
            <div
                onPointerDown={onResizeStart}
                style={{ touchAction: 'none' }}
                className="w-full h-1 bg-gray-700 hover:bg-gray-600 cursor-ns-resize transition-colors flex items-center justify-center group"
                title="Drag to resize panel"
            >
                <div className="w-12 h-0.5 bg-gray-500 group-hover:bg-gray-400 rounded-full transition-colors"></div>
            </div>
            
            {/* Tab Header */}
            <div>
                <div className="flex items-center justify-between px-3 pt-3">
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={() => onTabChange('progress')}
                            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-lg ${
                                activeTab === 'progress'
                                    ? 'bg-gray-800 text-white'
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Progress
                        </button>
                        <button
                            onClick={() => onTabChange('logs')}
                            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-lg ${
                                activeTab === 'logs'
                                    ? 'bg-gray-800 text-white'
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Live
                        </button>
                    </div>
                    <button
                        onClick={onToggle}
                        className="text-gray-400 hover:text-white p-1"
                        title="Close Panel"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {activeTab === 'progress' ? (
                    <BuildProgressTab
                        progressStatusMessages={progressStatusMessages}
                        aiActivitySummary={aiActivitySummary}
                        progressTabEndRef={progressTabEndRef}
                    />
                ) : (
                    <BuildLogsTab
                        dockerLogs={dockerLogs}
                        processedLogs={processedLogs}
                        onClearLogs={onClearLogs}
                        onToggleConnection={onToggleConnection}
                    />
                )}
            </div>
        </div>
    );
};

export default BuildBottomPanel;
